import get from 'lodash/get'
import { LayoutApi } from '@/api/layoutApi'
import store from '@/store/store'
import { defaultLayout, extensionThresholdKeys, defaultExtensionThresholdConfig } from '@/enum/default-layout-config'
import { convertHex } from '@/helpers/convertHex'
import {
    DEFAULT_LOGO,
    ENABLED_STATUS_ID,
    globalAccountSettings,
} from '@/views/DashboardSettings/LayoutManagement/layout-management'
import i18n from '@/i18n'

const types = {
    SET_ALL_LAYOUTS: 'SET_ALL_LAYOUTS',
    SET_ACTIVE_LAYOUT: 'SET_ACTIVE_LAYOUT',
    UPDATE_ACTIVE_LAYOUT: 'UPDATE_ACTIVE_LAYOUT',
    SET_GLOBAL_LAYOUT: 'SET_GLOBAL_LAYOUT',
    SET_PREVIEW_LAYOUT: 'SET_PREVIEW_LAYOUT',
    RESET_ACTIVE_LAYOUT: 'RESET_ACTIVE_LAYOUT',
    SET_TYPE_OF_LAYOUT: 'SET_TYPE_OF_LAYOUT'
};
const state = {
    data: [],
    activeLayout: {},
    globalLayout: defaultLayout(null),
    previewLayout: {},
    typeOfLayout: 'activeLayout'
};

const mutations = {
    [types.SET_ACTIVE_LAYOUT]: (state, value) => {
        state.activeLayout = value;
    },
    [types.SET_ALL_LAYOUTS]: (state, value) => {
        state.data = value;
    },
    [types.UPDATE_ACTIVE_LAYOUT]: (state, layout) => {
        state.activeLayout = layout;
    },
    [types.SET_GLOBAL_LAYOUT]: (state, value) => {
        if (!state.data.find(layout => layout.LayoutID === value.LayoutID)) {
            state.data.splice(0, 0, value)
        }

        state.globalLayout = value
    },
    [types.SET_PREVIEW_LAYOUT]: (state, value) => {
        state.previewLayout = value;
    },
    [types.RESET_ACTIVE_LAYOUT]: (state) => {
        state.previewLayout = {};
    },
    [types.SET_TYPE_OF_LAYOUT]: (state, typeOfLayout) => {
        state.typeOfLayout = typeOfLayout;
    }
};

const actions = {
    async setupActiveLayout({ commit, dispatch }) {
        const dashboard = store.state.dashboards.activeDashboard
        if (dashboard) {
            const { DashboardLayoutID } = dashboard
            const data = {
                LayoutID: DashboardLayoutID,
            }

            try {
                const layouts = await LayoutApi.get(data)

                if (layouts[0]) {
                    commit(types.SET_ACTIVE_LAYOUT, layouts[0])
                }
                return
            } catch (e) {
                const layouts = await LayoutApi.get({ LayoutAccountID: 0 })

                if (layouts[0]) {
                    commit(types.SET_ACTIVE_LAYOUT, layouts[0])
                    return
                }
            } finally {
                dispatch('setFavicon')
            }
        }

        const layout = defaultLayout(null)
        commit(types.SET_ACTIVE_LAYOUT, layout)
    },
    async setupLayouts({ commit }) {
        const accountID = store.state.entities.selectedAccountID
        const layouts = await LayoutApi.get({ LayoutAccountID: accountID })
        commit(types.SET_ALL_LAYOUTS, layouts)
    },
    async getGlobalLayout({ commit }) {
        const layout = await LayoutApi.get(globalAccountSettings)
        if (!layout[0]) {
            return
        }
        commit(types.SET_GLOBAL_LAYOUT, layout[0])
    },
    async updateActiveLayout({ commit }, layout) {
        commit(types.UPDATE_ACTIVE_LAYOUT, layout)
    },
    async setLogo({ commit, state }, config) {
        if (!state.activeLayout.LayoutParametersList) {
            return
        }
        const logoParameterIndex = state.activeLayout.LayoutParametersList.findIndex(el => el.LayoutParameterName === 'DashboardLogo')
        if (logoParameterIndex !== -1) {
            let logoParameter = state.activeLayout.LayoutParametersList[logoParameterIndex]
            logoParameter['ValueText'] = config
            commit(types.UPDATE_ACTIVE_LAYOUT, state.activeLayout)
        }
    },
    async setFavicon({ commit, state }, config) {
        if (!state.activeLayout.LayoutParametersList) {
            return
        }
        const logoParameterIndex = state.activeLayout.LayoutParametersList.findIndex(el => el.LayoutParameterName === 'Favicon')
        if (logoParameterIndex !== -1) {
            const logoParameter = state.activeLayout.LayoutParametersList[logoParameterIndex]
            if (config) {
                logoParameter['ValueText'] = config
                return
            }
            if (state.activeLayout.LayoutAccountID === 0) {
                logoParameter['ValueText'] = i18n.t('branding.Favicon')
            }
            const favicon = document.getElementById("favicon");
            favicon.href = logoParameter.ValueText
        }
    },
    async setPreviewLayout({ commit }, layout) {
        commit(types.SET_PREVIEW_LAYOUT, layout)
    },
    async resetPreviewLayout({ commit }) {
        commit(types.RESET_ACTIVE_LAYOUT)
    },
    async setTypeOfLayout({ commit }, typeOfLayout) {
        commit(types.SET_TYPE_OF_LAYOUT, typeOfLayout)
    }
};

const getters = {
    colors: (state) => (layoutType) => {
        let result = {
            primary: '#2575FF',
            primary_rgba: '37, 117, 255',
            backgroundLight: '#ffffff',
            backgroundDark: '#ffffff',
            frames: '#edf2f7',
            widgetGroupBackground: '#edf2f7',
            widgetGroupFrames: '#edf2f7',
            widgetGroupTitles: '#000000',
            loginStatusLimit: '#FF3636',
            loginStatusWarning: '#FAB11E',
            inCall: '#5EB300',
            onHold: '#E53E3E',

            mainBgColor: '#FFFFFF',
            secondaryBgColor: '#FFFFFF',
            itemsMainColor: '#E53E3E',
            itemsSecondaryColor: '#f5f7fa',
            cardsBgColor: '#f5f7fa',
            mainTextColor: '#000000',
            colorWidgetHeaderText: '#899398',
            colorWidgetBackground: '#f7fafc',
            colorTableHeaderBg: '#FFFFFF',
            colorTableContentText: '#000000',
            colorWidgetBorder: '#BEC2C9'
        }

        result['primary_rgba'] = convertHex(result.primary);

        if (!get(state, `${layoutType}.LayoutParametersList.length`, false)) {
            return result
        }

        state[layoutType].LayoutParametersList.forEach((el) => {

            if (el.LayoutParameterName === 'ColorPrimary') {
                result.primary = el.Value
            }

            if (el.LayoutParameterName === 'ColorBackground') {
                result.background = el.Value
            }

            if (el.LayoutParameterName === 'ColorFrames') {
                result.frames = el.Value
            }

            if (el.LayoutParameterName === 'ColorWidgetGroupBackground') {
                result.widgetGroupBackground = el.Value
            }

            if (el.LayoutParameterName === 'ColorWidgetGroupFrames') {
                result.widgetGroupFrames = el.Value
            }

            if (el.LayoutParameterName === 'ColorWidgetGroupTitles') {
                result.widgetGroupTitles = el.Value
            }

            if (el.LayoutParameterName === 'LoginStatusLimitColor') {
                result.loginStatusLimit = el.Value
            }

            if (el.LayoutParameterName === 'LoginStatusWarningColor') {
                result.loginStatusWarning = el.Value
            }

            if (el.LayoutParameterName === 'InCallColor') {
                result.inCall = el.Value
            }

            if (el.LayoutParameterName === 'OnHoldColor') {
                result.onHold = el.Value
            }

            if (el.LayoutParameterName === 'MainBgColor') {
                result.mainBgColor = el.Value
            }

            if (el.LayoutParameterName === 'SecondaryBgColor') {
                result.secondaryBgColor = el.Value
            }

            if (el.LayoutParameterName === 'ItemsMainColor') {
                result.itemsMainColor = el.Value
            }

            if (el.LayoutParameterName === 'ItemsSecondaryColor') {
                result.itemsSecondaryColor = el.Value
            }

            if (el.LayoutParameterName === 'CardsBgColor') {
                result.cardsBgColor = el.Value
            }

            if (el.LayoutParameterName === 'MainTextColor') {
                result.mainTextColor = el.Value
            }

            if (el.LayoutParameterName === 'ColorWidgetHeaderText') {
                result.colorWidgetHeaderText = el.Value
            }

            if (el.LayoutParameterName === 'ColorWidgetBackground') {
                result.colorWidgetBackground = el.Value
            }

            if (el.LayoutParameterName === 'ColorTableHeaderBg') {
                result.colorTableHeaderBg = el.Value
            }

            if (el.LayoutParameterName === 'ColorTableContentText') {
                result.colorTableContentText = el.Value
            }
            if (el.LayoutParameterName === 'WidgetBorderColor') {
                result.colorWidgetBorder = el.Value
            }
        });

        result['primary_rgba'] = convertHex(result.primary);

        return result
    },
    baseFontSize: (state) => (layoutType) => {
        const DEFAULT_VALUE = 16
        try {
            if (!state[layoutType].LayoutParametersList) {
                return DEFAULT_VALUE
            }
            let result = state[layoutType].LayoutParametersList.filter((el) => el.LayoutParameterName === 'FontSize')
            return Number(result[0]['Value'])
        } catch (e) {
            console.warn(e)
            return DEFAULT_VALUE
        }
    },
    refreshDelay: (state) => (layoutType) => {
        const DEFAULT_VALUE = 30
        try {
            if (!state[layoutType].LayoutParametersList) {
                return DEFAULT_VALUE
            }
            let result = state[layoutType].LayoutParametersList.filter((el) => el.LayoutParameterName === 'RefreshRealTimeDataDelay')
            return Number(result[0]['Value'])
        } catch (e) {
            console.warn(e)
            return DEFAULT_VALUE
        }
    },
    switchInterval: (state) => (layoutType) => {
        const DEFAULT_VALUE = null
        try {
            if (!state[layoutType].LayoutParametersList) {
                return DEFAULT_VALUE
            }
            let result = state[layoutType].LayoutParametersList.filter((el) => el.LayoutParameterName === 'ReportInterval')
            return Number(result[0]['Value'])
        } catch (e) {
            console.warn(e)
            return DEFAULT_VALUE
        }
    },
    switchReport: (state) => (layoutType) => {
        const DEFAULT_VALUE = false
        try {
            if (!state[layoutType].LayoutParametersList) {
                return DEFAULT_VALUE
            }
            let result = state[layoutType].LayoutParametersList.filter((el) => el.LayoutParameterName === 'ReportSwitching')
            return Boolean(result[0]['Value'] === '1');
        } catch (e) {
            console.warn(e)
            return DEFAULT_VALUE
        }
    },
    widgetGroupTitleStyles: (state) => (layoutType) => {
        const DEFAULT_VALUE = {
            color: '#000000',
            fontSize: '22px',
        }
        try {
            if (!state[layoutType].LayoutParametersList) {
                return DEFAULT_VALUE
            }
            const fontSize = state[layoutType].LayoutParametersList.filter((el) => el.LayoutParameterName === 'WidgetGroupTitlesFontSize')
            const _fontSize = Number(fontSize[0]['Value'])
            const color = state[layoutType].LayoutParametersList.filter((el) => el.LayoutParameterName === 'ColorWidgetGroupTitles')
            const _color = color[0]['Value']

            return {
                color: _color,
                fontSize: `${_fontSize}px`,
            }
        } catch (e) {
            console.warn(e)
            return DEFAULT_VALUE
        }
    },
    widgetTitleStyles: (state) => (layoutType) => {
        const DEFAULT_VALUE = {
            fontSize: '22px',
        }
        try {
            if (!state[layoutType].LayoutParametersList) {
                return DEFAULT_VALUE
            }
            const result = state[layoutType].LayoutParametersList.filter((el) => el.LayoutParameterName === 'WidgetTitlesFontSize')
            const fontSize = Number(result[0]['Value'])
            return {
                fontSize: `${fontSize}px`,
            }
        } catch (e) {
            console.warn(e)
            return DEFAULT_VALUE
        }
    },
    widgetTableContentFontSize: (state) => (layoutType) => {
        const DEFAULT_VALUE = {
            fontSize: '16px',
        }
        try {
            if (!state[layoutType].LayoutParametersList) {
                return DEFAULT_VALUE
            }
            const result = state[layoutType].LayoutParametersList.filter((el) => el.LayoutParameterName === 'WidgetTableContentFontSize')
            const fontSize = Number(result[0]['Value'])
            return {
                fontSize: `${fontSize}px`,
            }
        } catch (e) {
            console.warn(e)
            return DEFAULT_VALUE
        }
    },
    showWidgetTitles: (state) => (layoutType) => {
        const DEFAULT_VALUE = true
        try {
            if (!state[layoutType].LayoutParametersList) {
                return DEFAULT_VALUE
            }
            let result = state[layoutType].LayoutParametersList.filter((el) => el.LayoutParameterName === 'ShowWidgetTitles')
            return Boolean(result[0]['Value'] === '1');
        } catch (e) {
            console.warn(e)
            return DEFAULT_VALUE
        }
    },
    minRefreshInterval: (state) => (layoutType) => {
        const DEFAULT_VALUE = 10
        try {
            if (!state[layoutType].LayoutParametersList) {
                return DEFAULT_VALUE
            }
            let result = state[layoutType].LayoutParametersList.filter((el) => el.LayoutParameterName === 'MinRefreshInterval')
            return Number(result[0]['Value'])
        } catch (e) {
            console.warn(e)
            return DEFAULT_VALUE
        }
    },
    getLogo: (state) => (layoutType) => {
        const DEFAULT_VALUE = i18n.t('branding.DashboardLogo')
        try {
            if (state.activeLayout.LayoutAccountID === 0) {
                return DEFAULT_VALUE
            } else {
                if (!state[layoutType].LayoutParametersList) {
                    return DEFAULT_VALUE
                }
                const logo = state[layoutType].LayoutParametersList.filter((el) => el.LayoutParameterName === 'DashboardLogo')
                return get(logo, `[0]['ValueText']`, i18n.t('branding.DashboardLogo'))
            }
        } catch (e) {
            console.warn(e)
            return DEFAULT_VALUE
        }
    },
    getFavicon: (state) => (layoutType) => {
        const DEFAULT_VALUE = i18n.t('branding.Favicon')
        try {
            if (state.activeLayout.LayoutAccountID === 0) {
                return DEFAULT_VALUE
            } else {
                if (!state[layoutType].LayoutParametersList) {
                    return DEFAULT_VALUE
                }
                const logo = state[layoutType].LayoutParametersList.filter((el) => el.LayoutParameterName === 'Favicon')
                return get(logo, `[0]['ValueText']`, i18n.t('branding.Favicon'))
            }
        } catch (e) {
            console.warn(e)
            return DEFAULT_VALUE
        }
    },

    getActiveLayout: state => {
        return state.activeLayout || defaultLayout
    },
    getAllLayouts: state => state.data,
    getActiveLayouts: state => {
        return state.data.filter(layout => layout.LayoutStatusID.toString() === ENABLED_STATUS_ID.toString())
    },
    storedDashboardLayout: state => LayoutID => {
        return state.data.find(layout => layout.LayoutID.toString() === LayoutID.toString())
    },
    getThresholdConfig: (state) => (layoutType) => {
        const { LayoutParametersList } = state[layoutType] || {}

        if (!LayoutParametersList) {
            return defaultExtensionThresholdConfig
        }

        let config = {}
        LayoutParametersList.forEach(el => {
            if (extensionThresholdKeys.includes(el.LayoutParameterName)) {
                config[el.LayoutParameterName] = el.Value
            }
        })
        return config
    },
    getPreviewLayout: state => {
        return state.previewLayout
    },
    getTypeOfLayout: state => {
        return state.typeOfLayout
    },
    getSoundFromLayout: (state) => (layoutType, layoutParameterName) => {
        const DEFAULT_VALUE = null
        if (!state[layoutType].LayoutParametersList) {
            return DEFAULT_VALUE
        }
        const sound = state[layoutType].LayoutParametersList.filter((el) => el.LayoutParameterName === layoutParameterName)

        if (!sound || !sound.length) {
            return DEFAULT_VALUE
        }

        const sounds = layoutParameterName.toLowerCase().includes('warning')  ? store.state.entities.warningSounds : store.state.entities.errorSounds
        const currentSound = sounds.find(el => el.value === sound[0].Value)

        if (!currentSound) {
            return DEFAULT_VALUE
        }
        return currentSound.sound
    }
}

export default {
    namespaced: true,
    state,
    mutations,
    getters,
    actions,
};


